import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { isErrorMessages } from '@core/interfaces';
import { InputComponent } from './input.component';
import { InputTypeEnum } from './enums';

@Injectable({
  providedIn: 'root',
})
export class InputPresenter<T> {
  view!: InputComponent<T>;
  InputTypeEnum = InputTypeEnum;
  showPassword = false;

  setView(view: InputComponent<T>): void {
    this.view = view;
  }

  onDestroy(): void {}

  get formControl(): FormControl {
    return (this.view.control as FormControl) ?? new FormControl();
  }

  get isInvalid(): boolean {
    return !!(this.view.control?.invalid && (this.view.control.dirty || this.view.control.touched));
  }

  getErrorMessage(): string | null {
    if (this.view.control) {
      const { errors } = this.view.control;
      if (errors) {
        const errorsList = Object.entries(errors);
        const [errorKey, errorValue] = errorsList.length > 0 ? errorsList[0] : [];
        if (errorKey && errorValue && this.view.errorMessages) {
          const controlId = this.view.controlId as string;
          if (controlId && isErrorMessages(this.view.errorMessages)) {
            const controlErrorMessages = this.view.errorMessages[controlId];
            if (isErrorMessages(controlErrorMessages)) {
              if (controlErrorMessages[errorKey]) {
                const errorMessage = controlErrorMessages[errorKey];
                if (typeof errorMessage === 'string') {
                  return errorMessage;
                } else if (typeof errorMessage === 'function') {
                  return errorMessage(errorValue);
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  getOptionProperty(option: T, key: string): unknown {
    return (option as Record<string, unknown>)[key];
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.view.value = target.value;
  }

  onDropdownChange(event: Event | string | unknown): void {
    const value = typeof event === 'string'
      ? event
      : (event && typeof event === 'object' && 'target' in event)
        ? (event.target as HTMLSelectElement).value
        : (event as string);
    this.view.value = value;
    this.view.dropdownChange.emit(value);
  }

  onCheckboxChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.view.value = target.checked;
  }

  onBlur(): void {
    this.view.onTouched();
  }

  onCalendarChange(value: Date): void {
    this.view.calendarChange.emit(value);
  }

  onSwitchChange(event: { checked: boolean }, data: T | null | undefined): void {
    if (!data) data = null;
    this.view.switchChange.emit({ checked: event.checked, data });
  }

  onElementChange(value: T): void {
    this.view.elementChange.emit(value);
  }

  onInputChange(value: T | string): void {
    this.view.inputChange.emit(value);
  }
  
  onMultidropdownChange(event: { value: T }): void {
    this.view.multidropdownChange.emit(event.value);
  }
  
  onLazyLoadDropdown(event: unknown): void {
    this.view.lazyLoadDropdownChange.emit(event);
  }

  onModelChange(value: string): void {
    this.view.modelChange.emit(value);
  }
}
